import { fixedDecimals } from '../fixedDecimals'

/**
 * An SVG path through `points` as a monotone cubic (Fritsch–Carlson): smooth, and it
 * never overshoots between two points, so a line through net figures cannot dip below
 * a month's real minimum. `d3-shape` is not a dependency, hence the few lines here.
 * Points must be in strictly increasing x.
 */
export function monotonePath(points: readonly (readonly [number, number])[]): string {
  const n = points.length
  const first = points[0]
  if (first === undefined) return ''
  // Two decimals, as the other charts write SVG coordinates.
  const r = (v: number): string => fixedDecimals(v, 2)
  if (n === 1) return `M${r(first[0])} ${r(first[1])}`

  const dx: number[] = []
  const slope: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    dx.push(b[0] - a[0])
    slope.push((b[1] - a[1]) / (b[0] - a[0]))
  }
  const tan: number[] = Array.from({ length: n }, () => 0)
  tan[0] = slope[0]!
  tan[n - 1] = slope[n - 2]!
  for (let i = 1; i < n - 1; i++) {
    const s0 = slope[i - 1]!
    const s1 = slope[i]!
    tan[i] = s0 * s1 <= 0 ? 0 : (s0 + s1) / 2
  }
  for (let i = 0; i < n - 1; i++) {
    const s = slope[i]!
    if (s === 0) {
      tan[i] = 0
      tan[i + 1] = 0
      continue
    }
    const a = tan[i]! / s
    const b = tan[i + 1]! / s
    const h = Math.hypot(a, b)
    if (h > 3) {
      tan[i] = (3 * a * s) / h
      tan[i + 1] = (3 * b * s) / h
    }
  }
  let d = `M${r(first[0])} ${r(first[1])}`
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    const w = dx[i]! / 3
    d += ` C${r(a[0] + w)} ${r(a[1] + tan[i]! * w)} ${r(b[0] - w)} ${r(b[1] - tan[i + 1]! * w)} ${r(b[0])} ${r(b[1])}`
  }
  return d
}
